/* eslint-disable no-unused-vars */
import { FC } from 'react';
import {
    Button,
    Space,
    Form,
    Input,
    Alert,
    Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import IMAGES from '../const/images';
import {
    startLogin,
    startValidateEmail,
    useAuthStore,
} from '../store/auth.store';
import { ILogin } from '../interfaces/auth.interface';
import { Footer } from '../components/Onboarding/Footer';
import { Header } from '../components/Onboarding/Header';

interface IProps {
    t: TFunction;
}

interface IFormContainerProps extends IProps {
    error: string | null;
    emailExist: boolean | null;
    loading: boolean;
    handleSubmit: (values: any) => void;
}

const { Title, Text } = Typography;

const FormContainer: FC<IFormContainerProps> = ({
    t,
    handleSubmit,
    error,
    emailExist,
    loading,
}) => (
    <Form onFinish={handleSubmit} layout="vertical">
        {
            error && (
                <Alert
                    message={error}
                    type="error"
                    style={{ marginBottom: 24 }}
                />
            )
        }

        <Form.Item
            name="email"
            label={t('EMAIL')}
            rules={[{ type: 'email', message: t('INVALID_EMAIL') }, { required: true, message: t('REQUIRED') }]}
        >
            <Input
                placeholder={t('EMAIL')}
            />
        </Form.Item>

        {
            emailExist !== null && !emailExist && (
                <Alert
                    message={(
                        <Space size={4}>
                            {t('USER_NOT_FOUND')}
                            <Link to="/signup">
                                {t('CREATE_AN_ACCOUNT')}
                            </Link>
                        </Space>
                    )}
                    type="info"
                    style={{ marginBottom: 24 }}
                />
            )
        }

        {
            emailExist && (
                <Form.Item
                    name="password"
                    label={t('PASSWORD')}
                    rules={[{ required: true, message: t('REQUIRED') }]}
                >
                    <Input.Password
                        type="password"
                        placeholder={t('PASSWORD')}
                    />
                </Form.Item>
            )
        }

        <Button
            block
            type="primary"
            htmlType="submit"
            loading={loading}
        >
            {emailExist ? t('LOGIN') : t('CONTINUE')}
        </Button>

    </Form>
);

const ResetPassword: FC<IProps> = ({ t }) => (
    <div className="flex-row center-row-center">
        <Space size={4}>
            <Text type="secondary">
                {t('FORGOT_TEXT')}
            </Text>
            <Link to="/reset-password" key="reset">
                {t('RESET_IT_HERE')}
            </Link>
        </Space>
    </div>
);

const LoginScreen = () => {
    const { t } = useTranslation('login');

    const emailExist = useAuthStore((state) => state.emailExist);
    const loading = useAuthStore((state) => state.loading);
    const error = useAuthStore((state) => state.error);

    const handleSubmit = async ({ email, password }: ILogin) => {
        if (emailExist) {
            startLogin(email, password);
            return;
        }
        startValidateEmail(email);
    };

    return (
        <div
            style={{ backgroundImage: `url(${IMAGES.login.spirals})` }}
            className="public-layout"
        >
            <div className="public-layout__container">
                <div className="public-layout__container--form">
                    <Space direction="vertical" size={40}>
                        <Header />
                        <Title
                            level={2}
                            style={{
                                color: '#6C8EEF',
                                marginBottom: 0,
                                textAlign: 'center',
                            }}
                        >
                            {t('LOGIN_TITLE')}
                        </Title>
                        <Space direction="vertical" size={24}>
                            <FormContainer
                                t={t}
                                handleSubmit={handleSubmit}
                                error={error}
                                emailExist={emailExist}
                                loading={loading}
                            />
                            <ResetPassword t={t} />
                        </Space>
                        <Footer />
                    </Space>
                </div>
            </div>
        </div>
    );
};

export { LoginScreen };
